/*
 * Project: bellcloud
 * File: apps/frontend/src/connections/serial/useSerial.tsx
 * Author: SWAN
 * Created: 2025-01-27 10:00:00
 * Description: Web Serial Provider - 기기 연결 관리
 */
import { createContext, useContext, useRef, useState } from 'react';
import type { PropsWithChildren } from 'react';
import type {
  GeneratePacketRequest,
  PacketToJsonResponse,
} from '@packages/types/device/common/api.common';
import type { DeviceInfo } from '@packages/types/device/lite/deviceInfo';
import { WebSerialAdapter } from './serialAdapter';
import { BAUD_RATE } from '@packages/protocols/constants';
import { SinglePacketMode, BurstMode } from '../modes';
import type { PacketApiAdapter } from '../modes/types';

// Serial 포트 상태 타입
type PortState = 'closed' | 'opening' | 'open' | 'closing';

// Serial 포트 정보 타입
interface SerialPortInfo {
  port: string;
  baudRate: number;
  vendorId: number;
  productId: number;
  manufacturer: string;
  serialNumber: string;
  productName: string;
  interfaceClass: number;
}
import { useGeneratePacket, useParsePacket } from '@/api/device/useLite';
import { canSyncPacketToCloud } from '@/utils/cloudSyncAuth';
import { useGenerateMiniPacket, useParseMiniPacket } from '@/api/device/useMini';
import { apiHelpers } from '@/api/core';
import { useAppStore } from '@/store/loadingStore';
import {
  LogDownloadHandler,
  type LogDownloadRequest,
  type LogDownloadResult,
} from '@/utils/logDownloadHandler';

interface SerialContextValue {
  canUseSerial: boolean;
  hasTriedAutoconnect: boolean;
  portState: PortState;
  error?: string;
  connect(): Promise<boolean>;
  disconnect(): void;
  scan(): Promise<SerialPortInfo[]>;
  scanAndConnect(baudRate?: number): Promise<boolean>;
  write(data: Uint8Array): Promise<void>;
  rawRequest(data: Uint8Array): Promise<Uint8Array>;
  connectDevice(
    dto: GeneratePacketRequest,
    deviceInfo?: DeviceInfo,
    sessionId?: string,
    onProgress?: (progress: number, current: number, total: number) => void
  ): Promise<PacketToJsonResponse>;
  downloadLog(
    request: LogDownloadRequest,
    sessionId: string,
    onProgress?: (progress: number, current: number, total: number) => void,
    chunkUploadCallback?: (
      logId: string,
      serialNumber: string,
      chunkIndex: number,
      chunkData: Uint8Array
    ) => Promise<void>,
    chunkBatchUploadCallback?: (
      logId: string,
      serialNumber: string,
      chunks: Array<{
        chunk_index: number;
        chunk_data: number[];
        crc?: number;
      }>
    ) => Promise<void>,
    batchSize?: number
  ): Promise<LogDownloadResult>;
  cancelDownload(): Promise<boolean>;
  isConnected(): boolean;
  clearBuffer(releaseReader?: boolean): void;
}

export { WebSerialProvider };

const SerialContext = createContext<SerialContextValue>({
  canUseSerial: false,
  hasTriedAutoconnect: false,
  connect: () => Promise.resolve(false),
  disconnect: () => {},
  portState: 'closed',
  write: async () => {},
  rawRequest: async () => new Uint8Array(),
  scan: () => Promise.resolve([]),
  scanAndConnect: () => Promise.resolve(false),
  connectDevice: async (
    _dto: GeneratePacketRequest,
    _deviceInfo?: DeviceInfo
  ): Promise<PacketToJsonResponse> => {
    return {} as PacketToJsonResponse;
  },
  downloadLog: async () => {
    return {
      success: false,
      log_id: '',
      file_type: 0,
      num_packets: 0,
      downloaded_packets: 0,
      chunks_stored: false,
      error_message: 'Not implemented',
    };
  },
  cancelDownload: async () => false,
  isConnected: () => false,
  clearBuffer: () => {},
});

export const useSerial = () => useContext(SerialContext);

interface WebSerialProviderProps {}

// 패킷 저장 헬퍼 함수 (백그라운드, 에러 무시)
const savePacketInBackground = async (serialNumber: string, packet: number[]) => {
  try {
    if (!canSyncPacketToCloud()) {
      return;
    }

    await apiHelpers.post('device.packet', {
      serial_number: serialNumber,
      packet,
    });
  } catch (error) {
    // 백그라운드 저장 실패는 조용히 무시
    console.warn('⚠️ [savePacketInBackground] Failed to save packet:', error);
  }
};

const WebSerialProvider = ({ children }: PropsWithChildren<WebSerialProviderProps>) => {
  // 로그 다운로드용 fileType 상태 저장
  const fileTypeRef = useRef<number | null>(null);
  const { mutateAsync: generatePacket } = useGeneratePacket();
  const { mutateAsync: parsePacket } = useParsePacket();
  const { mutateAsync: generateMiniPacket } = useGenerateMiniPacket();
  const { mutateAsync: parseMiniPacket } = useParseMiniPacket();
  const { updateConnectedDevice, disconnectDevice, getAnyConnectedDevice } = useAppStore();
  const [canUseSerial] = useState(() => 'serial' in navigator);
  const [portState, setPortState] = useState<PortState>('closed');

  const [error, setError] = useState<string | undefined>(undefined);

  const adapterRef = useRef<WebSerialAdapter | null>(null);
  const downloadHandlerRef = useRef<LogDownloadHandler | null>(null);

  // PacketApiAdapter 생성 (Mode에서 사용)
  const createPacketApi = (isMiniDevice: boolean): PacketApiAdapter => ({
    generatePacket: isMiniDevice ? generateMiniPacket : generatePacket,
    parsePacket: isMiniDevice ? parseMiniPacket : parsePacket,
  });

  const initializeHandler = () => {
    if (!adapterRef.current) {
      adapterRef.current = new WebSerialAdapter();
    }
  };

  const scanAndConnect = async (baudRate: number = BAUD_RATE): Promise<boolean> => {
    if (!canUseSerial) {
      console.warn('[Serial] browser does not support Web Serial');
      return false;
    }

    // 이미 연결되어 있으면 성공 반환
    if (isConnected()) {
      setPortState('open'); // portState도 함께 업데이트
      return true;
    }

    // 연결 중이면 대기
    if (portState === 'opening') {
      return false;
    }

    setPortState('opening');
    setError(undefined);

    try {
      initializeHandler();

      if (!adapterRef.current) {
        throw new Error('Serial adapter 초기화 실패');
      }

      // Web Serial에서는 사용자가 포트를 선택하므로 'selected'로 표시
      await adapterRef.current.connect('selected', baudRate);

      setPortState('open');
      return true;
    } catch (error) {
      console.error('[Serial] failed to connect', error);
      setPortState('closed');

      // 에러 메시지 추출
      const errorMessage = error instanceof Error ? error.message : '연결 실패';
      setError(errorMessage);

      return false;
    }
  };

  const scan = async (): Promise<SerialPortInfo[]> => {
    if (!canUseSerial) return [];

    try {
      initializeHandler();
      if (!adapterRef.current) {
        throw new Error('Serial adapter 초기화 실패');
      }
      return await adapterRef.current.scan();
    } catch (error) {
      // console.error('[Serial] failed to scan ports', error);
      return [];
    }
  };

  const manualconnectPort = async () => {
    return scanAndConnect(BAUD_RATE);
  };

  const manualDisconnectFromPort = async () => {
    if (!canUseSerial) {
      console.warn('[Serial] browser does not support Web Serial ');
      return;
    }

    if (portState !== 'open') {
      return;
    }

    if (!adapterRef.current) {
      console.warn('[Serial] adapter is not initialized');
      setPortState('closed');
      return;
    }

    // 연결된 기기의 serialNumber 찾기
    const connectedDevice = getAnyConnectedDevice();
    const serialNumber =
      connectedDevice?.serialNumber || connectedDevice?.deviceData?.serial_number;

    setPortState('closing');
    try {
      await adapterRef.current.disconnect();

      // 전역 상태에서 연결 해제
      if (serialNumber) {
        disconnectDevice(serialNumber, 'serial');
      }
    } catch (e) {
      console.error('[Serial] failed to disconnect', e);
    } finally {
      setPortState('closed');
    }
  };

  const write = async (data: Uint8Array): Promise<void> => {
    if (!adapterRef.current) {
      throw new Error('Serial adapter is not initialized');
    }
    await adapterRef.current.write(data);
  };

  // JSON 파싱 없이 순수 패킷 왕복
  const rawRequest = async (data: Uint8Array): Promise<Uint8Array> => {
    if (!adapterRef.current) {
      throw new Error('Serial adapter가 초기화되지 않았습니다');
    }
    if (!adapterRef.current.isOpen()) {
      throw new Error('Serial port is not open. Please connect to the Serial port first.');
    }
    await adapterRef.current.write(data);
    // 응답 수신을 위한 타임아웃 설정
    const response = await adapterRef.current.readWithTimeout(1200);
    return response;
  };

  const isConnected = (): boolean => {
    return adapterRef.current?.isOpen() || false;
  };

  const clearBuffer = (releaseReader: boolean = true): void => {
    if (adapterRef.current) {
      adapterRef.current.clearBuffer(releaseReader);
    }
  };

  const connectDevice = async (
    dto: GeneratePacketRequest,
    deviceInfo?: DeviceInfo,
    _sessionId?: string,
    onProgress?: (progress: number, current: number, total: number) => void
  ): Promise<PacketToJsonResponse> => {
    if (!adapterRef.current) {
      throw new Error('Serial adapter is not initialized');
    }

    // 실제 어댑터 연결 상태만 확인 (portState 무시)
    if (!adapterRef.current.isOpen()) {
      throw new Error('Serial port is not open. Please connect to the Serial port first.');
    }

    // 디바이스 타입 확인 (serial_number에 'BMI'가 포함되어 있으면 mini)
    const isMiniDevice = deviceInfo?.serial_number?.toUpperCase().includes('BMI') || false;

    // Class 16 요청인지 확인
    const isClass16 = dto.class_id === 16;

    // Class 16, function 1은 로그 다운로드 모드로 간주
    const isLogDownloadMode = isClass16 && dto.function_id === 1;

    // 버스트 모드 다운로드 중에는 다른 요청 차단 (Class 16이 아닌 요청만)
    const adapter = adapterRef.current as any;
    if (adapter?.isDownloading && adapter?.mode === 'stream' && !isClass16) {
      console.warn(
        `⚠️ [SerialProvider] 버스트 모드 다운로드 중입니다. connectDevice() 요청이 차단되었습니다. ` +
          `큐=${adapter.queueLength || 0}, 버퍼=${adapter.bufferSize || 0} bytes`
      );
      throw new Error('버스트 모드 다운로드 중에는 다른 요청을 보낼 수 없습니다');
    }

    // class 16, function 1 요청이면 payload에서 fileType 추출 및 저장
    if (dto.class_id === 16 && dto.function_id === 1 && dto.json_payload) {
      const fileType = dto.json_payload.file_type;
      if (typeof fileType === 'number') {
        fileTypeRef.current = fileType;
      }
    }

    try {
      // Mode 선택: Class 16이면 BurstMode, 아니면 SinglePacketMode
      const packetApi = createPacketApi(isMiniDevice);
      const mode = isClass16
        ? new BurstMode(packetApi, savePacketInBackground)
        : new SinglePacketMode(packetApi, savePacketInBackground);

      // Mode 실행
      const result = await mode.execute(adapterRef.current, {
        deviceInfo: deviceInfo || null,
        isMiniDevice,
        dto,
        fileType: fileTypeRef.current,
        isLogDownloadMode,
        onProgress,
      });

      if (!result.success) {
        throw result.error || new Error('통신 실패');
      }

      const jsonResponse = result.response;
      const headerPacket = result.headerPacket;

      // class 16, function 2 응답이면 payload에서 fileType 추출 및 저장
      if (headerPacket) {
        const actualClassId = headerPacket[4];
        const actualFunctionId = headerPacket[5];

        if (actualClassId === 16 && actualFunctionId === 2) {
          const fileType = headerPacket[8];
          if (typeof fileType === 'number') {
            fileTypeRef.current = fileType;
          }
        }
      }

      // 기기 정보 요청(class_id: 1, function_id: 1)인 경우 전역 상태에 저장
      if (dto.class_id === 1 && dto.function_id === 1 && jsonResponse.json) {
        const deviceData = jsonResponse.json as any;
        if (deviceData.serial_number) {
          try {
            // 다른 serialNumber의 모든 연결 해제 (다른 기기로 전환 시)
            const connectedDevice = getAnyConnectedDevice();
            const newSerialNumber = deviceData.serial_number;

            if (connectedDevice && connectedDevice.serialNumber) {
              if (connectedDevice.serialNumber !== newSerialNumber) {
                // 모든 연결 타입 해제
                connectedDevice.connections.forEach(conn => {
                  if (conn.isConnected && connectedDevice.serialNumber) {
                    disconnectDevice(connectedDevice.serialNumber, conn.type);
                  }
                });
              } else if (
                connectedDevice.serialNumber === newSerialNumber &&
                connectedDevice.connections.some(c => c.type === 'ble' && c.isConnected)
              ) {
                // 같은 기기지만 다른 연결 타입(BLE)이 있으면 해제
                disconnectDevice(connectedDevice.serialNumber, 'ble');
              }
            }

            updateConnectedDevice(deviceData.serial_number, 'serial', {
              name: deviceData.serial_number,
              serialNumber: deviceData.serial_number,
              ...deviceData,
            });
          } catch (error) {
            console.error('❌ [Serial Provider] 기기 정보 저장 실패:', error);
          }
        } else {
          console.warn('⚠️ [Serial Provider] serial_number 없음:', deviceData);
        }
      }

      return jsonResponse;
    } catch (error) {
      // 에러 발생 시에도 모드 복귀
      if (adapterRef.current) {
        adapterRef.current.setSinglePacketMode();
      }
      console.error('❌ [connectDevice] 송수신 실패:', error);
      throw error;
    }
  };

  /**
   * 로그 다운로드 전용 함수
   * Class 16 통신을 담당하며, 청크를 파싱하지 않고 세션에 저장
   * @param chunkUploadCallback 스트리밍 업로드 콜백 (청크를 받는 즉시 전송)
   */
  const downloadLog = async (
    request: LogDownloadRequest,
    sessionId: string,
    onProgress?: (progress: number, current: number, total: number) => void,
    chunkUploadCallback?: (
      logId: string,
      serialNumber: string,
      chunkIndex: number,
      chunkData: Uint8Array
    ) => Promise<void>,
    chunkBatchUploadCallback?: (
      logId: string,
      serialNumber: string,
      chunks: Array<{
        chunk_index: number;
        chunk_data: number[];
        crc?: number;
      }>
    ) => Promise<void>,
    batchSize?: number
  ): Promise<LogDownloadResult> => {
    if (!adapterRef.current) {
      throw new Error('Serial adapter가 초기화되지 않았습니다');
    }

    if (!adapterRef.current.isOpen()) {
      throw new Error('Serial 포트가 연결되지 않았습니다');
    }

    // 디바이스 타입 확인
    const isMiniDevice = request.serial_number?.toUpperCase().includes('BMI') || false;

    // 패킷 API 어댑터 생성
    const packetApi = {
      generatePacket: isMiniDevice ? generateMiniPacket : generatePacket,
      parsePacket: isMiniDevice ? parseMiniPacket : parsePacket,
    };

    // LogDownloadHandler 생성 (배치 콜백 포함)
    const handler = new LogDownloadHandler(
      adapterRef.current as any,
      packetApi as any,
      isMiniDevice,
      chunkUploadCallback,
      chunkBatchUploadCallback,
      batchSize
    );

    // Store handler reference for cancellation
    downloadHandlerRef.current = handler;

    // 로그 다운로드 실행
    return handler.downloadLog(request, sessionId, onProgress);
  };

  const cancelDownload = async (): Promise<boolean> => {
    // If handler exists, use it (BLE/downloadLog case)
    if (downloadHandlerRef.current) {
      try {
        const result = await downloadHandlerRef.current.cancelDownload();
        downloadHandlerRef.current = null;
        return result;
      } catch (error) {
        console.error('❌ [Serial] Failed to cancel download via handler:', error);
        return false;
      }
    }

    // If no handler, send 0x16 directly via adapter (Cradle/connectDevice case)
    if (!adapterRef.current) {
      console.warn('⚠️ [Serial] No adapter available');
      return false;
    }

    try {
      // Generate stop burst mode packet (Class 16, Function 0x16)
      const stopPacket = await generatePacket({
        class_id: 16,
        function_id: 0x16,
        json_payload: {},
      });

      // Send stop command via adapter (with retry)
      if (adapterRef.current.stopBurstMode) {
        const maxRetries = 3;
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            const response = await adapterRef.current.stopBurstMode(
              new Uint8Array(stopPacket.packet)
            );

            // Parse response (0x17)
            const parsed = await parsePacket({
              class_id: 16,
              function_id: 0x17,
              packet: Array.from(response),
            });

            const result = parsed.json?.Result || 0;
            if (result === 1) {
              return true;
            } else if (result === 0) {
              // Result=0: Device may not be in burst mode, but that's OK for cancel
              return true;
            } else {
              console.warn(
                `⚠️ [Serial] Stop burst mode failed: Result=${result} (attempt ${attempt})`
              );
              if (attempt < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms before retry
              }
            }
          } catch (error) {
            console.error(`❌ [Serial] Error on attempt ${attempt}:`, error);
            if (attempt < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
        }

        console.warn('⚠️ [Serial] All stop burst mode attempts failed');
        return false;
      } else {
        console.warn('⚠️ [Serial] Adapter does not support stopBurstMode');
        return false;
      }
    } catch (error) {
      console.error('❌ [Serial] Failed to send 0x16 packet:', error);
      return false;
    }
  };

  return (
    <SerialContext.Provider
      value={{
        canUseSerial,
        hasTriedAutoconnect: false,
        portState,
        error,
        connect: manualconnectPort,
        disconnect: manualDisconnectFromPort,
        scan,
        scanAndConnect,
        write,
        rawRequest,
        connectDevice,
        downloadLog,
        cancelDownload,
        isConnected,
        clearBuffer,
      }}
    >
      {children}
    </SerialContext.Provider>
  );
};

